import type { ClienteFiltro, FiltroKpi, OrdenCasos, Vista } from "./CasosFiltros";
import { etiquetaFiltroKpi } from "./CasosFiltros";
import type { EstadoCaso } from "@/lib/api";

type Posesion = "todos" | "mios" | "nosotros" | "cliente" | "proveedor";

export type FiltrosActivosProps = {
  vista: Vista;
  estadoFilter: EstadoCaso | "todos";
  setEstadoFilter: (v: EstadoCaso | "todos") => void;
  clienteFiltro: ClienteFiltro | null;
  setClienteFiltro: (v: ClienteFiltro | null) => void;
  agrupar: boolean;
  setAgrupar: (v: boolean) => void;
  esperaMiRespuesta: boolean;
  setEsperaMiRespuesta: (v: boolean) => void;
  soloCriticos: boolean;
  setSoloCriticos: (v: boolean) => void;
  proveedorPendiente: boolean;
  setProveedorPendiente: (v: boolean) => void;
  asesorFiltro: { id: number; nombre: string } | null;
  setAsesorFiltro: (v: { id: number; nombre: string } | null) => void;
  marcaFiltro: string | null;
  setMarcaFiltro: (v: string | null) => void;
  posesionFilter: Posesion;
  setPosesionFilter: (v: Posesion) => void;
  nombreEmpresa: string;
  filtroKpi: FiltroKpi | null;
  onClearFiltroKpi: () => void;
  orden: OrdenCasos;
  setOrden: (v: OrdenCasos) => void;
  onLimpiarTodo: () => void;
};

const ORDEN_LABEL: Record<OrdenCasos, string> = {
  recientes: "Recién creados",
  antiguos: "Más antiguos",
  estancados: "Más estancados",
  movidos: "Movidos hace poco",
};

/**
 * Lo que está filtrando AHORA, con su ✕ para quitarlo de un toque.
 *
 * Existe por el viaje que costaba en el celular: los filtros viven dentro de un
 * sheet, así que para quitar uno había que abrirlo, buscarlo, apagarlo y
 * cerrarlo — el mismo recorrido completo que para ponerlo. Aquí se quitan sin
 * entrar, y de paso se ve qué está aplicado sin tener que acordarse.
 *
 * Solo se muestra en móvil: en escritorio los controles ya están todos a la
 * vista y estas fichas serían ruido duplicado.
 */
export default function FiltrosActivos(p: FiltrosActivosProps) {
  type Ficha = { key: string; label: string; quitar: () => void };
  const fichas: Ficha[] = [];

  if (p.filtroKpi) {
    fichas.push({
      key: "kpi",
      label: `Estadísticas: ${etiquetaFiltroKpi(p.filtroKpi)}`,
      quitar: p.onClearFiltroKpi,
    });
  }
  if (p.estadoFilter !== "todos") {
    fichas.push({
      key: "estado",
      label: p.estadoFilter,
      quitar: () => p.setEstadoFilter("todos"),
    });
  }
  if (p.clienteFiltro) {
    fichas.push({
      key: "cliente",
      label: p.clienteFiltro.nombre,
      quitar: () => p.setClienteFiltro(null),
    });
  }
  if (p.posesionFilter !== "todos") {
    const etiqueta: Record<Exclude<Posesion, "todos">, string> = {
      mios: "Míos",
      nosotros: p.nombreEmpresa,
      cliente: "Con el cliente",
      proveedor: "Con el proveedor",
    };
    fichas.push({
      key: "posesion",
      label: etiqueta[p.posesionFilter],
      quitar: () => p.setPosesionFilter("todos"),
    });
  }
  if (p.esperaMiRespuesta) {
    fichas.push({
      key: "espera",
      label: "Esperan mi respuesta",
      quitar: () => p.setEsperaMiRespuesta(false),
    });
  }
  if (p.soloCriticos) {
    fichas.push({
      key: "critico",
      label: "Equipo crítico",
      quitar: () => p.setSoloCriticos(false),
    });
  }
  if (p.proveedorPendiente) {
    fichas.push({
      key: "proveedor",
      label: "El proveedor ya contestó",
      quitar: () => p.setProveedorPendiente(false),
    });
  }
  if (p.asesorFiltro) {
    fichas.push({
      key: "asesor",
      label: `Asesor: ${p.asesorFiltro.nombre}`,
      quitar: () => p.setAsesorFiltro(null),
    });
  }
  if (p.marcaFiltro) {
    fichas.push({
      key: "marca",
      label: p.marcaFiltro,
      quitar: () => p.setMarcaFiltro(null),
    });
  }
  if (p.agrupar) {
    fichas.push({
      key: "agrupar",
      label: "Agrupado por cliente",
      quitar: () => p.setAgrupar(false),
    });
  }
  if (p.orden !== "recientes") {
    fichas.push({
      key: "orden",
      label: ORDEN_LABEL[p.orden],
      quitar: () => p.setOrden("recientes"),
    });
  }

  if (fichas.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {fichas.map((f) => (
        <button
          key={f.key}
          type="button"
          onClick={f.quitar}
          aria-label={`Quitar el filtro ${f.label}`}
          className="inline-flex max-w-full items-center gap-1 rounded-full border border-accent-500/40 bg-accent-500/15 px-2 py-0.5 text-[11px] text-accent-300 transition hover:bg-accent-500/25"
        >
          <span className="truncate">{f.label}</span>
          <span aria-hidden className="shrink-0 text-accent-400">
            ✕
          </span>
        </button>
      ))}
      {fichas.length > 1 && (
        <button
          type="button"
          onClick={p.onLimpiarTodo}
          className="text-[11px] text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline"
        >
          Limpiar todo
        </button>
      )}
    </div>
  );
}
